import { addDays, formatISO } from 'date-fns'

// The content v2 endpoint caps page size at 500. Re-running the full
// permission resolution per page is the dominant per-page cost, so fetching
// in 500s (rather than a lower amount) cuts the number of pages — and thus
// that repeated work — for a heavy week.
export const CONTENT_PAGE_LIMIT = 500

// Asset references are only needed to render the per-card preview image,
// which shows only when In-Market Duration is off. Skipping them when it's on
// (the default) drops a sizable chunk of per-page serialization.
const contentFieldsAndIncludes = (includeAssets) => ({
  fields: {
    content: [
      'account',
      ...(includeAssets ? ['asset_references'] : []),
      'inherited_labels',
      'is_published',
      'labels',
      'moment',
      'name',
      'position',
      'service',
      'status',
      'scheduled_at',
      'start_interval',
      'takedown_scheduled_at',
    ].join(','),
    account: 'name',
    service: 'name,system_name',
    label: 'color,label_set,name,position',
    label_set: 'name',
    ...(includeAssets && {
      // Just enough to resolve a preview image via
      // `getRelevantImageProperties` (proxy URL is built from `uuid`);
      // covers both the asset and its cover.
      asset_reference:
        'asset_type,cover_asset_reference,format,height,uuid,width',
    }),
  },
  include: [
    'account',
    'service',
    // Include `labels` explicitly (not just the nested `labels.label_set`):
    // the V2 content preloader only deep-preloads a label's `label_set` and
    // `label_color` when `labels` itself is a requested include — its
    // `requested?` check is exact, so a nested-only `labels.label_set` left
    // both N+1'ing. Redundant for serialization.
    'labels',
    'labels.label_set',
    // Posts often carry their labels via the parent moment/account/story
    // rather than directly. The grid filter matches on `inherited_labels`
    // (see MomentFilter), so include it or label-filtering hides those posts.
    'inherited_labels',
    ...(includeAssets ? ['asset_references.cover_asset_reference'] : []),
  ],
})

/*
 * Builds parameters for fetching content.
 */
export const getContentQueryParams = ({
  filter,
  page,
  omit_count,
  includeAssets = false,
}) => {
  // Pad the fetch by a day on each side. A post renders on the day its
  // timezone-adjusted `scheduled_at` lands on (see `dateAdjustedForTimezone`),
  // which can differ from its stored date by a day for viewers far from the
  // workspace timezone — e.g. a post on the week's first day in Auckland has a
  // stored (Pacific) date in the prior day. Without the pad the server filters
  // it out and it never reaches the grid (`include_overlapping_schedules` only
  // helps posts with a takedown range). Placement re-filters to the visible
  // week, so the extra day of content is harmless.
  const startDate = addDays(filter.scheduled_start_at, -1)
  const endDate = addDays(filter.scheduled_start_at, 7)
  return {
    filter: {
      brand_id: filter.brand_id,
      scheduled_start_at: formatISO(startDate, { representation: 'date' }),
      scheduled_end_at: formatISO(endDate, { representation: 'date' }),
      story_id: filter.story_id,
      use_moment_schedule: filter.use_moment_schedule,
      // Surface posts whose in-market window overlaps the visible week
      // even if scheduled_at is outside — `PostSchedule.overlaps_date_range`.
      include_overlapping_schedules: true,
      // V2 content endpoint accepts `filter[search_terms]` (plural) as a
      // whitelisted attribute on `Posts::ReadCollection`. Only include it
      // when the user has a non-empty search term so the unfiltered case
      // stays as-is.
      ...(typeof filter.search_terms === 'string' &&
        filter.search_terms.length > 0 && {
          search_terms: filter.search_terms,
        }),
    },
    ...contentFieldsAndIncludes(includeAssets),
    page: {
      limit: CONTENT_PAGE_LIMIT,
      offset: page.offset,
    },
    omit_count,
  }
}

// About 1.2 kB of numeric ids in `filter[id]`, well inside proxy request-line
// limits.
export const CONTENT_ID_CHUNK_SIZE = 150

/*
 * Builds parameters for fetching content by id, for the content on the
 * moments in view. Unlike the week's date query, it finds content dated
 * outside the week, and a lookup by id costs the database far less.
 */
export const getContentByIdQueryParams = ({
  ids,
  brandId,
  storyId,
  includeAssets = false,
}) => ({
  filter: {
    brand_id: brandId,
    id: ids.join(','),
    story_id: storyId,
  },
  ...contentFieldsAndIncludes(includeAssets),
  page: {
    limit: CONTENT_PAGE_LIMIT,
    offset: 0,
  },
  omit_count: true,
})

export const getMomentsQueryParams = ({
  brandId,
  startDate,
  storyId,
  scopeToCurrentBrand,
  searchString,
}) => {
  /* basic params necessary for all fetches */
  // here we add 7 days instead of 6, which means we are
  // fetching a total range of 8 days. This is needed
  // to ensure that all timezones see the expected moments
  // in their week.
  const endDate = addDays(startDate, 7)
  const basicParams = {
    'filter[start_date]': formatISO(startDate),
    'filter[end_date]': formatISO(endDate),
    // Range-intersection fetch: surface moments whose duration overlaps
    // the visible week even when `scheduled_at` sits outside it. Required
    // for Content mode's gap-detection workflow on multi-week campaigns,
    // where the parent moment may start weeks before its posts render.
    'filter[include_duration_overlap]': true,
    'page[limit]': 999999,
    'page[offset]': 0,
    'fields[moment]': [
      'account',
      'asset_references',
      'brand',
      'color',
      'content_count',
      'day_segment',
      'is_ghost',
      'is_visible',
      'labels',
      'posts',
      'primary_asset_reference',
      'primary_story',
      'privacy_group',
      'scheduled_at',
      'scheduled_end_at',
      'stories',
      'services',
      'title',
      'uuid',
    ].join(','),
    'fields[asset_reference]': [
      'asset_type',
      'cover_asset_reference',
      'escaped_filename',
      'filename',
      'format',
      'full_url',
      'height',
      'preview_url',
      'uuid',
      'width',
    ].join(','),
    'fields[brand]': ['logo', 'name', 'primary_label_set', 'slug', 'uuid'].join(
      ','
    ),
    'fields[label_set]': ['name', 'uuid', 'labels'].join(','),
    'fields[label]': ['color', 'label_set', 'name', 'position', 'uuid'].join(
      ','
    ),
    'fields[service]': ['name', 'system_name'].join(','),
    'fields[story]': [
      'brand',
      'brands',
      'is_shared',
      'name',
      'scheduled_end_at',
      'scheduled_start_at',
      'uuid',
    ].join(','),
  }
  if (storyId) {
    basicParams['filter[story_id]'] = storyId
  }

  /* params necessary for full moment fetch */
  const includes = [
    'asset_references.cover_asset_reference',
    'brand',
    // Powers the label-color stripe on ContentCard.
    'brand.primary_label_set',
    'labels.label_set',
    'primary_asset_reference.cover_asset_reference',
    'privacy_group.pending_members',
    'services',
    'stories',
  ]

  const fetchingParams = {
    include: includes.join(','),
  }

  /* params necessary for index fetch */
  const indexingParams = {
    ...(typeof searchString === 'string' &&
      searchString.length > 0 && { 'filter[search]': searchString }),
  }
  const fetchType = 'default'
  if (fetchType === 'index') {
    return {
      indexingOnlyFetch: true,
      params: {
        ...basicParams,
        ...indexingParams,
      },
      currentBrandId: brandId,
      scopeToCurrentBrand,
    }
  }
  return {
    params: {
      ...basicParams,
      ...indexingParams,
      ...fetchingParams,
    },
    currentBrandId: brandId,
    scopeToCurrentBrand,
    component: 'weekView',
  }
}

export const getLabelSetsQueryParams = ({
  currentBrandId,
  scopeToCurrentBrand,
}) => {
  const params = {
    'filter[label_set]': 100,
    'page[limit]': 100,
  }
  return { currentBrandId, params, scopeToCurrentBrand, component: 'weekView' }
}
